/**
 * A dropdown menu: closes on outside click, on Escape, and on selection.
 *
 * Used for the organization switcher and per-row action menus. The arrow
 * keys move between items (Home and End jump to the ends), so a keyboard user
 * never has to Tab through every item to reach the last one.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';

export function Menu({ trigger, children, align = 'right', label = 'Actions' }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!anchorRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const onMenuKeyDown = (event) => {
    const items = [...(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') ?? [])];
    if (!items.length) return;
    const index = items.indexOf(document.activeElement);
    const next = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: items.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    items[(next + items.length) % items.length]?.focus();
  };

  const renderTrigger =
    typeof trigger === 'function'
      ? trigger
      : ({ toggle }) => (
          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={toggle} aria-label={label} aria-haspopup="menu" aria-expanded={open}>
            <Icon name="more" size={16} />
          </button>
        );

  return (
    <div className="menu-anchor" ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
      {renderTrigger({ open, toggle: () => setOpen((value) => !value) })}
      {open && (
        <div
          ref={menuRef}
          className={`menu ${align === 'left' ? 'menu-left' : ''}`}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
        >
          {/* Any click inside closes the menu, so no item has to remember to. */}
          <div onClick={() => setOpen(false)} role="none">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

export function MenuItem({ onClick, danger, destructive, active, children, ...rest }) {
  const isDanger = danger || destructive;
  return (
    <button
      type="button"
      className={`menu-item ${isDanger ? 'menu-item-danger' : ''} ${active ? 'menu-item-active' : ''}`}
      onClick={onClick}
      role="menuitem"
      {...rest}
    >
      {children}
    </button>
  );
}

export function MenuLabel({ children }) {
  return <div className="menu-label">{children}</div>;
}

export function MenuDivider() {
  return <div className="menu-divider" role="separator" />;
}
